import { isEmpty, omit } from "lodash"
import { push } from "react-router-redux"
import { call, put, takeLatest } from "redux-saga/effects"
import SignaturePad from "signature_pad/dist/signature_pad.js"
import * as gql from "../../../common/graphql"
import { PICTURE_HEIGHT, PICTURE_WIDTH } from "../PicturesPage"
import { removeCase } from "./actions"
import {
  DAMAGE_CASE_SUBMIT_CASE_BEGIN,
  DAMAGE_CASE_SUBMIT_CASE_DISMISS_ERROR,
  DAMAGE_CASE_SUBMIT_CASE_FAILURE,
  DAMAGE_CASE_SUBMIT_CASE_SUCCESS,
} from "./constants"

export function submitCase(caseId, caseData, authToken, { forceManualProcessing } = {}) {
  return {
    type: DAMAGE_CASE_SUBMIT_CASE_BEGIN,
    data: { caseId, caseData, authToken, forceManualProcessing },
  }
}

export function dismissSubmitCaseError(caseId) {
  return {
    type: DAMAGE_CASE_SUBMIT_CASE_DISMISS_ERROR,
    data: { caseId },
  }
}

// worker Saga: will be fired on DAMAGE_CASE_SUBMIT_CASE_BEGIN actions
export function* doSubmitCase({ data: { caseId, caseData, authToken, forceManualProcessing } }) {
  caseData = {
    ...caseData,
    copiesToContactAddressCodes: caseData.copiesToContactAddressCodes.filter(c => c !== caseData.contactAddressCode),
    pictures: yield applyDrawingsAsync(caseData.pictures),
  }

  const { data, graphqlErrors, networkError } = yield call(
    gqlSubmitCase,
    caseId,
    caseData,
    authToken,
    forceManualProcessing,
  )

  if (data) {
    yield put({
      type: DAMAGE_CASE_SUBMIT_CASE_SUCCESS,
      data: { caseId },
    })
    yield put(removeCase(caseId))
    yield put(push("/"))
  } else {
    yield put({
      type: DAMAGE_CASE_SUBMIT_CASE_FAILURE,
      data: { caseId, error: { graphqlErrors, networkError } },
    })
  }
}

export function gqlSubmitCase(caseId, caseData, authToken, forceManualProcessing) {
  return gql.post({
    authToken,
    queryName: "submitDamageCase",
    query: `mutation submitDamageCase($clientSideId: String, $damageCaseData: DamageCaseData!, $forceManualProcessing: Boolean) {
      submitDamageCase(clientSideId: $clientSideId, damageCaseData: $damageCaseData, forceManualProcessing: $forceManualProcessing) {
        clientSideId
      }
    }`,
    variables: {
      clientSideId: caseId,
      damageCaseData: caseData,
      forceManualProcessing,
    },
  })
}

// NB: Setting the src on an img is always async and we couldn't find a synchronous way to paint a base64 image onto
//     a canvas and we needed a canvas because we didn't want to meddle with the private API of SignaturePad
function* applyDrawingsAsync(pictures) {
  function loadImageAsync(pictureDataURL) {
    return new Promise((resolve, reject) => {
      const img = new Image()
      img.onload = () => resolve(img)
      img.onerror = reject
      img.src = pictureDataURL
    })
  }

  if (isEmpty(pictures)) {
    return pictures
  } else {
    const pictureCanvas = document.createElement("canvas")
    pictureCanvas.width = PICTURE_WIDTH
    pictureCanvas.height = PICTURE_HEIGHT
    const pictureCanvas2d = pictureCanvas.getContext("2d")

    const drawingCanvas = document.createElement("canvas")
    drawingCanvas.width = pictureCanvas.width
    drawingCanvas.height = pictureCanvas.height
    const signaturePad = new SignaturePad(drawingCanvas)

    const picturesWithDrawings = []
    for (let idx = 0; idx < pictures.length; idx++) {
      const { base64: pictureDataURL, drawingData, timestamp } = pictures[idx]

      if (drawingData) {
        const img = yield loadImageAsync(pictureDataURL)

        pictureCanvas2d.drawImage(img, 0, 0)

        signaturePad.fromData(drawingData)
        pictureCanvas2d.drawImage(drawingCanvas, 0, 0)

        const combinedDataURL = pictureCanvas.toDataURL("image/jpeg")

        picturesWithDrawings.push({ base64: combinedDataURL, timestamp })
      } else {
        picturesWithDrawings.push({ base64: pictureDataURL, timestamp })
      }
    }

    return picturesWithDrawings
  }
}

/*
  Alternatively you may use takeEvery.

  takeLatest does not allow concurrent requests. If an action gets
  dispatched while another is already pending, that pending one is cancelled
  and only the latest one will be run.
*/
export function* watchSubmitCase() {
  yield takeLatest(DAMAGE_CASE_SUBMIT_CASE_BEGIN, doSubmitCase)
}

// Redux reducer
export function reducer(state, action) {
  switch (action.type) {
    case DAMAGE_CASE_SUBMIT_CASE_BEGIN:
      return {
        ...state,
        submitCasePending: [...state.submitCasePending, action.data.caseId],
        submitCaseError: omit(state.submitCaseError, action.data.caseId),
      }

    case DAMAGE_CASE_SUBMIT_CASE_SUCCESS:
      return {
        ...state,
        submitCasePending: state.submitCasePending.filter(item => item !== action.data.caseId),
        submitCaseError: omit(state.submitCaseError, action.data.caseId),
      }

    case DAMAGE_CASE_SUBMIT_CASE_FAILURE:
      return {
        ...state,
        submitCasePending: state.submitCasePending.filter(item => item !== action.data.caseId),
        submitCaseError: { ...state.submitCaseError, [action.data.caseId]: action.data.error },
      }

    case DAMAGE_CASE_SUBMIT_CASE_DISMISS_ERROR:
      return {
        ...state,
        submitCaseError: omit(state.submitCaseError, action.data.caseId),
      }

    default:
      return state
  }
}



// WEBPACK FOOTER //
// ./src/features/damage-case/redux/submitCase.js